import React, { memo, useEffect, useState } from 'react';
import styles from './CompanyFirstLogin.module.scss';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import CommonSelect, { CommonSelectItem } from '../../../common/components/select/CommonSelect';
import FormWrapper from '../../../common/components/form-wrapper/FormWrapper';
import CommonFormFieldWrapper, { getFormInputErrors } from '../../../common/components/form-field-wrapper/CommonFormFieldWrapper';
import { useDispatch } from 'react-redux';
import { setFirstLogin } from '../../../stores/slices/first-login-slice';
import { ApplicationRoutes } from '../../../routes';
import { trimFormValues } from '../../../common/utils/trimFormValues';
import { useGetStatesByCountryQuery } from '../../../service/state-service';
import { useFillCompanyInfoMutation } from '../../../service/solar-provider-service';
import { useLazyGetCompanyInfoQuery } from '../../../service/company-service';

type Props = {
}

type ProviderCompany = {
  name: string
  businessInfo: string
  businessType: string
  rcNumber: string
  address: string
  state?: string
  city: string
}

const CompanyFirstLogin: React.FC<Props> = memo((props: Props) => {
  let { companyId = "" } = useParams();
  const [states, setStates] = useState<CommonSelectItem[]>([])
  const [selectedState, setSelectedState] = useState<CommonSelectItem>()
  const [countryCode, setCountryCode] = useState("");
  const [ getCompanyInfo, { isLoading: isCheckingFirstLogin }] = useLazyGetCompanyInfoQuery()
  const [fillCompanyInfo, { isLoading: isSaving }] = useFillCompanyInfoMutation();
  const { data: statesData, error: statesError } = useGetStatesByCountryQuery(countryCode, { skip: !countryCode });
  
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const {
    register,
    watch,
    formState: { errors, isValid },
    setValue,
    getValues
  } = useForm<ProviderCompany>({
    defaultValues: {
      name: '',
      businessInfo: '',
      businessType: '',
      rcNumber: '',
      address: '',
      state: undefined,
      city: ''
    },
    reValidateMode: 'onSubmit',
    mode: 'onTouched'
  })

  useEffect(() => {
    const checkFirstLogin = async () => {
        try {
          const response = await getCompanyInfo({companyId}).unwrap();
            
            if (!response.firstLogin) {
                navigate(`/${ApplicationRoutes.PROVIDER_BASE}/${ApplicationRoutes.DASHBOARD}`);
            }

            dispatch(setFirstLogin(true));
            setValue('name', response.name);
            if (response.country) {
                setCountryCode(response.country);
            }
        } catch (error) {
            toast.error("Failed to check first login status");
        }
    };

    checkFirstLogin();
}, [companyId, navigate, setValue, dispatch, getCompanyInfo]);

useEffect(() => {
  if (statesData) {
      const stateOptions: CommonSelectItem[] = statesData.map((state) => 
          new CommonSelectItem(state.id, state.name)
      );
      setStates(stateOptions);
  }
  if (statesError) {
      toast.error("There was an error fetching states");
  }
}, [statesData, statesError]);

  async function onSubmit() {
    if (!isValid) return; 

    try {
      await fillCompanyInfo(trimFormValues(getValues())).unwrap();
      toast.success("Company Information updated successfully")
      dispatch(setFirstLogin(false));
      navigate(`/${ApplicationRoutes.PROVIDER_BASE}/${ApplicationRoutes.DASHBOARD}`);
    }
    catch {
      return toast.error('An error occurred while trying to save the information');
    }
  }

  const changeValue = (value: CommonSelectItem | undefined) => {
    setValue('state', value?.label)
    setSelectedState(states.find(x => x.label === watch('state')))
  }

  


  return (
    <div className={styles.layout}>
      <FormWrapper
        titleSection={{ title: 'Business Information', subtitle: "Tell us about your business.", buttonTitle: 'Proceed'}}
        isNotValid={!isValid || isCheckingFirstLogin || isSaving} 
        onSaveAndContinue={onSubmit} >
        <div className={styles.mainContainer}>
          <CommonFormFieldWrapper title="Business Name"
            required
            disabled
            errors={getFormInputErrors(errors.name?.message)}>
            <input disabled {...register('name', { required: 'Business Name is required' })} placeholder="Business Name" type='text'/>
          </CommonFormFieldWrapper>
          <CommonFormFieldWrapper title="Business Info"
            errors={getFormInputErrors(errors.businessInfo?.message)}>
            <input {...register('businessInfo', { required: 'Business Info is required' })} placeholder="Enter Business Info" type='text' />
          </CommonFormFieldWrapper>
          <CommonFormFieldWrapper title="Business Type"
            required
            errors={getFormInputErrors(errors.businessType?.message)}>
            <input {...register('businessType', { required: 'Business Type is required' })} placeholder="Enter Business Type" type='text' />
          </CommonFormFieldWrapper>
          <CommonFormFieldWrapper title="RC Number"
            required
            errors={getFormInputErrors(errors.rcNumber?.message)}>
            <input {...register('rcNumber', { required: 'RC Number is required', pattern: { value: /^\d+$/, message: 'RC Number must contain only numbers' } })} placeholder="Enter RC Number" type='text' />
          </CommonFormFieldWrapper>
          <CommonFormFieldWrapper title="Street Address"
            required
            errors={getFormInputErrors(errors.address?.message)}>
            <input {...register('address', { required: 'Street Address is required' })} placeholder="Enter Street Address" type='text'  />
          </CommonFormFieldWrapper>

          <div className="formFieldInlineContainer">
            <CommonFormFieldWrapper title="State" required>
                <CommonSelect 
                registerProp={register('state', { required: 'State is required' })} 
                items={states} 
                selected={selectedState} 
                placeholder='Select' 
                searchable={true}
                onChange={changeValue} />
            </CommonFormFieldWrapper>
            <CommonFormFieldWrapper title="City/Town" required>
                <input value={watch('city')} {...register('city', { required: 'City is required' })} placeholder="Enter City/Town Name" />
            </CommonFormFieldWrapper>
          </div>
        </div>
      </FormWrapper>
    </div> 
  )
})

export default CompanyFirstLogin